import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export type CertificateHolder = {
  databaseId: string;
  name: string;
  eMail: string;
  cellPhone: string;
  phone: string;
  fax: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateName: string;
  zipCode: string;
};

export function useCertificateHolders(search: string = '', top: number = 12, skip: number = 0) {
  const [holders, setHolders] = useState<CertificateHolder[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchHolders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const NCToken = localStorage.getItem('NCToken');
    const igtoken = localStorage.getItem('igtoken');
    if (!igtoken) {
      setError('Not authenticated. Please sign in again.');
      setIsLoading(false);
      return;
    }
    try {
      let url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-holders?top=${top}&skip=${skip}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, {
        headers: {
          'NCToken': NCToken || '',
          'Accept': 'application/json',
          'Authorization': `Bearer ${igtoken}`,
        },
      });
      if (res.status === 401) {
        window.localStorage.clear();
        navigate('/');
        return;
      }
      if (!res.ok) throw new Error('Failed to fetch certificate holders');
      const data = await res.json();
      const ncUserId = localStorage.getItem('NCUserId');
      const records = (data.value || []).filter((h: any) => h.lastChangeUserId === ncUserId);
      setHolders(records);
      setTotalCount(records.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load certificate holders.');
    } finally {
      setIsLoading(false);
    }
  }, [search, top, skip, navigate]);

  useEffect(() => {
    fetchHolders();
  }, [fetchHolders]);

  return { holders, totalCount, isLoading, error, refetch: fetchHolders };
}
